import {
    Box,
    BoxProps,
    IconButton,
    InputAdornment,
    Menu,
    MenuItem,
    Stack,
    Tooltip,
    useMediaQuery,
} from '@mui/material';
import { Event as Calendar, ExpandCircleDown as ExpandCircleDownIcon } from '@mui/icons-material';
import {
    DateRange,
    DatePicker,
    DateRangePicker as MuiDateRangePicker,
    DateRangePickerToolbarProps,
    LocalizationProvider,
    PickersShortcutsItem,
    RangePosition,
} from '@mui/x-date-pickers-pro';
import { AdapterDateFns } from '@mui/x-date-pickers-pro/AdapterDateFns';
import { differenceInDays, differenceInMilliseconds, endOfDay, startOfDay } from 'date-fns';
import type { Theme } from '@mui/material/styles';
import { FC, KeyboardEvent, useEffect, useMemo, useRef, useState } from 'react';
import { Button } from '../Button';
import { FCNC } from '../FCNC';
import { useFieldsReadonly } from '../Form';
import { ReadOnly } from '../ReadOnly';
import { ITooltipIconProps, renderTooltipLabel } from '../Tooltip';
import { getDatesFromShortcut, DateRangePickerShortcut } from '../DateRangePicker/DateRangePicker.utils';
import { ICustomTheme } from '../themes';
import { useBrandVersion } from '../../hooks';

enum DateRangePickerShortcutStrings {
    TODAY = 'Today',
    YESTERDAY = 'Yesterday',
    PAST_WEEK = 'Past Week',
    PAST_MONTH = 'Past Month',
    PAST_3_MONTHS = 'Past 3 Months',
    PAST_6_MONTHS = 'Past 6 Months',
}

const DateRangePickerShortcutStringToEnumTable: { [key in DateRangePickerShortcutStrings]: DateRangePickerShortcut } = {
    [DateRangePickerShortcutStrings.TODAY]: DateRangePickerShortcut.TODAY,
    [DateRangePickerShortcutStrings.YESTERDAY]: DateRangePickerShortcut.YESTERDAY,
    [DateRangePickerShortcutStrings.PAST_WEEK]: DateRangePickerShortcut.PAST_WEEK,
    [DateRangePickerShortcutStrings.PAST_MONTH]: DateRangePickerShortcut.PAST_MONTH,
    [DateRangePickerShortcutStrings.PAST_3_MONTHS]: DateRangePickerShortcut.PAST_3_MONTHS,
    [DateRangePickerShortcutStrings.PAST_6_MONTHS]: DateRangePickerShortcut.PAST_6_MONTHS,
};

const DATE_RANGE_PICKER_SHORTCUT: { [key in DateRangePickerShortcut]: PickersShortcutsItem<DateRange<Date>> } = {
    [DateRangePickerShortcut.TODAY]: {
        label: DateRangePickerShortcutStrings.TODAY,
        getValue: () => getDatesFromShortcut(DateRangePickerShortcut.TODAY),
    },
    [DateRangePickerShortcut.YESTERDAY]: {
        label: DateRangePickerShortcutStrings.YESTERDAY,
        getValue: () => getDatesFromShortcut(DateRangePickerShortcut.YESTERDAY),
    },
    [DateRangePickerShortcut.PAST_WEEK]: {
        label: DateRangePickerShortcutStrings.PAST_WEEK,
        getValue: () => getDatesFromShortcut(DateRangePickerShortcut.PAST_WEEK),
    },
    [DateRangePickerShortcut.PAST_MONTH]: {
        label: DateRangePickerShortcutStrings.PAST_MONTH,
        getValue: () => getDatesFromShortcut(DateRangePickerShortcut.PAST_MONTH),
    },
    [DateRangePickerShortcut.PAST_3_MONTHS]: {
        label: DateRangePickerShortcutStrings.PAST_3_MONTHS,
        getValue: () => getDatesFromShortcut(DateRangePickerShortcut.PAST_3_MONTHS),
    },
    [DateRangePickerShortcut.PAST_6_MONTHS]: {
        label: DateRangePickerShortcutStrings.PAST_6_MONTHS,
        getValue: () => getDatesFromShortcut(DateRangePickerShortcut.PAST_6_MONTHS),
    },
};

export interface IDateRangePickerFieldProps {
    value: DateRange<Date>;
    onChange: (date: DateRange<Date>, shortcut: DateRangePickerShortcut | null) => void;
    placeholders?: [string, string];
    maxDate?: Date;
    minDate?: Date;
    testId?: string;
    id?: string;
    stacked?: boolean;
    label?: string;
    labelBoxSx?: BoxProps['sx'];
    tooltip?: ITooltipIconProps;
    readonly?: boolean;
    required?: boolean;
    shortcuts?: DateRangePickerShortcut[];
    /** Sizes the control to its fields (e.g. dashboard chart header) instead of stretching to full row width. */
    compactInline?: boolean;
}

const CustomDateRangePickerToolbar = ({ onChange, value }: DateRangePickerToolbarProps<Date> & { onChange: any }) => {
    const onClick = (start: Date, end: Date, diff: number) => () => {
        onChange([new Date(start.getTime() + diff), new Date(end.getTime() + diff)]);
    };

    if (value[0] instanceof Date && value[1] instanceof Date) {
        const days = Math.abs(differenceInDays(value[0], value[1]));
        const diff = Math.abs(differenceInMilliseconds(value[0], value[1]));

        const months = Math.floor(days / 30);
        const label = months ? (months === 1 ? '1 month' : `${months} months`) : days === 1 ? '1 day' : `${days} days`;

        return (
            days >= 1 && (
                <Box
                    sx={{
                        gridColumn: '2',
                        display: 'flex',
                        justifyContent: 'space-between',
                        padding: 2,
                    }}
                >
                    <Button onClick={onClick(value[0], value[1], -diff)}>previous {label}</Button>
                    <Button onClick={onClick(value[0], value[1], +diff)}>next {label}</Button>
                </Box>
            )
        );
    }

    return null;
};

interface IPresetsMenuProps {
    shortcuts: DateRangePickerShortcut[];
    onPick: (shortcut: DateRangePickerShortcut) => void;
}

const PresetsMenu: FC<IPresetsMenuProps> = ({ shortcuts, onPick }) => {
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
    const open = Boolean(anchorEl);

    return (
        <>
            <Tooltip title="Quick ranges" componentsProps={{ tooltip: { sx: { fontSize: '14px' } } }}>
                <IconButton
                    size="medium"
                    onClick={(e) => setAnchorEl(e.currentTarget)}
                    aria-haspopup="menu"
                    aria-expanded={open ? 'true' : undefined}
                    aria-label="Quick ranges"
                    sx={{ alignSelf: 'center' }}
                >
                    <ExpandCircleDownIcon />
                </IconButton>
            </Tooltip>
            <Menu anchorEl={anchorEl} open={open} onClose={() => setAnchorEl(null)}>
                {shortcuts.map((shortcut) => {
                    const item = DATE_RANGE_PICKER_SHORTCUT[shortcut];
                    return (
                        <MenuItem
                            key={shortcut}
                            onClick={() => {
                                onPick(shortcut);
                                setAnchorEl(null);
                            }}
                        >
                            {item.label}
                        </MenuItem>
                    );
                })}
            </Menu>
        </>
    );
};

export const DateRangePickerField: FCNC<IDateRangePickerFieldProps> = ({
    stacked,
    shortcuts = [],
    placeholders,
    required = false,
    readonly,
    labelBoxSx,
    tooltip,
    label,
    id,
    value,
    onChange,
    maxDate,
    minDate,
    testId,
    compactInline = false,
}) => {
    const isReadonly = useFieldsReadonly(readonly);
    const isMobile = useMediaQuery((t: ICustomTheme) => t.breakpoints.down('mobile'));
    const isSmDown = useMediaQuery((t: Theme) => t.breakpoints.down('sm'));

    // TODO: remove this once the legacy brand is retired
    const isNewBrand = useBrandVersion() === 'newBrand';

    // only show individual errors if range picker is required
    const [startError, setStartError] = useState(required && value[0] === null);
    const [endError, setEndError] = useState(required && value[1] === null);

    const shortcutsItems = isMobile ? [] : shortcuts.map((shortcut) => DATE_RANGE_PICKER_SHORTCUT[shortcut]);
    let shortcutSelected: DateRangePickerShortcut | null = null;

    const fixedValue = useMemo((): DateRange<Date> => {
        return [value?.[0] instanceof Date ? value[0] : null, value?.[1] instanceof Date ? value[1] : null];
    }, [value]);

    const handleChange = (date: DateRange<Date>, ctx: any) => {
        const [start, end] = date;

        if (required) {
            setStartError(!start);
            setEndError(!end);
        }

        // check date validity normalized to start of day
        if (
            start &&
            ((minDate && startOfDay(start) < startOfDay(minDate)) ||
                (maxDate && startOfDay(start) > startOfDay(maxDate)))
        ) {
            setStartError(true);
        } else {
            setStartError(false);
        }

        if (
            end &&
            ((minDate && startOfDay(end) < startOfDay(minDate)) || (maxDate && startOfDay(end) > startOfDay(maxDate)))
        ) {
            setEndError(true);
        } else {
            setEndError(false);
        }

        if (ctx && ctx.shortcut) {
            shortcutSelected =
                DateRangePickerShortcutStringToEnumTable[
                    ctx.shortcut.label as keyof typeof DateRangePickerShortcutStringToEnumTable
                ];
        } else {
            shortcutSelected = null;
        }
    };

    const handleAccept = (date: DateRange<Date>) => {
        let [start, end] = date;

        if (start !== null && end !== null) {
            start = startOfDay(start);
            end = endOfDay(end);
            if (minDate) {
                if (start < minDate) {
                    start = minDate;
                }

                if (end < minDate) {
                    end = minDate;
                }
            }
        }

        if (required) {
            setStartError(!start);
            setEndError(!end);
        }

        onChange([start, end], shortcutSelected);
    };

    const getErrorStatusForDateField = useMemo(
        () => (position: RangePosition | undefined) => {
            if (required && position) {
                return position === 'start' ? startError : endError;
            }
            return false;
        },
        [required, startError, endError]
    );

    const pendingRangeRef = useRef<DateRange<Date>>(fixedValue);

    useEffect(() => {
        pendingRangeRef.current = fixedValue;
    }, [fixedValue]);

    if (isReadonly) {
        return (
            <ReadOnly
                testId={testId}
                label={label}
                value={`${value[0] instanceof Date ? value[0].toLocaleString() : ''} - ${
                    value[1] instanceof Date ? value[1]?.toLocaleString() : ''
                }`}
            />
        );
    }

    if (required && label) {
        label = `${label} *`;
    }

    // The visible caption labels the Start/End pair as a group. The MUI pickers render their own
    // inputs (with their own "Start Date"/"End Date" labels and auto-generated ids), so a
    // <label htmlFor={id}> points at an id that never exists — an orphaned label. Caption +
    // role="group" associates the two inputs with "Date Sent Range" correctly instead.
    const groupLabelId = id ? `${id}-label` : undefined;

    if (isNewBrand) {
        const isValidPickerDate = (date: Date | null): date is Date =>
            date instanceof Date && !Number.isNaN(date.getTime());

        const rangeMatchesValue = (range: DateRange<Date>, baseline: DateRange<Date>) => {
            const toTime = (d: Date | null) => (isValidPickerDate(d) ? d.getTime() : null);
            return toTime(range[0]) === toTime(baseline[0]) && toTime(range[1]) === toTime(baseline[1]);
        };

        const commitRangeIfComplete = (range: DateRange<Date>) => {
            const [start, end] = range;
            if (!isValidPickerDate(start) || !isValidPickerDate(end)) {
                return;
            }
            if (rangeMatchesValue(range, fixedValue)) {
                return;
            }
            handleAccept(range);
        };

        const handleSinglePickerChange = (next: DateRange<Date>) => {
            pendingRangeRef.current = next;
            handleChange(next, undefined);
        };

        const commitOnFieldBlur = () => commitRangeIfComplete(pendingRangeRef.current);

        const commitOnFieldKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
            if (event.key === 'Enter') {
                event.preventDefault();
                commitRangeIfComplete(pendingRangeRef.current);
            }
        };

        // Remount pickers when either bound changes so controlled values stay in sync after shortcuts.
        const pickerSyncKey = `${fixedValue[0]?.getTime() ?? 's'}_${fixedValue[1]?.getTime() ?? 'e'}`;

        const inlineHeader = Boolean(compactInline && !stacked);
        const fullWidthFields = !inlineHeader || isSmDown;

        const sharedInputSx = inlineHeader
            ? {
                  minWidth: 0,
                  width: { xs: '100%', sm: 154 },
                  maxWidth: '100%',
                  '.MuiInputBase-input': { caretColor: 'auto' },
              }
            : {
                  width: '100%',
                  minWidth: 0,
                  '.MuiInputBase-input': { caretColor: 'auto' },
              };
        const openPickerSlotProps = {
            sx: {
                '&.Mui-focusVisible, &:focus-visible': {
                    outline: (theme: Theme) => `2px solid ${theme.palette.primary.main}`,
                    outlineOffset: 2,
                },
            },
        };

        const mobileToolbarTitleSx = {
            '& > .MuiTypography-overline': {
                textTransform: 'none' as const,
                letterSpacing: 'normal',
                fontSize: (theme: Theme) => theme.typography.subtitle1.fontSize ?? '1rem',
                lineHeight: (theme: Theme) => theme.typography.subtitle1.lineHeight ?? 1.5,
                fontWeight: (theme: Theme) => theme.typography.fontWeightMedium ?? 500,
            },
        };

        const startTextFieldSlot = {
            fullWidth: fullWidthFields,
            'data-testid': `${testId}-start`,
            inputProps: { 'data-testid': `${testId}-start` },
            error: getErrorStatusForDateField('start'),
            helperText: getErrorStatusForDateField('start') && required ? 'Required' : undefined,
            sx: sharedInputSx,
            onBlur: commitOnFieldBlur,
            onKeyDown: commitOnFieldKeyDown,
        };
        const endTextFieldSlot = {
            fullWidth: fullWidthFields,
            'data-testid': `${testId}-end`,
            inputProps: { 'data-testid': `${testId}-end` },
            error: getErrorStatusForDateField('end'),
            helperText: getErrorStatusForDateField('end') && required ? 'Required' : undefined,
            sx: sharedInputSx,
            onBlur: commitOnFieldBlur,
            onKeyDown: commitOnFieldKeyDown,
        };

        const dateColSx = inlineHeader
            ? {
                  flex: { xs: 'none', sm: '0 0 auto' },
                  minWidth: { xs: '100%', sm: 0 },
                  width: { xs: '100%', sm: 'auto' },
              }
            : {
                  flex: { xs: 'none', mobile: '1 1 0%' },
                  // Let fields shrink to fit their parent cell; otherwise two 156px columns + label
                  // overflow narrow form rows and crash into the next column.
                  minWidth: { xs: '100%', mobile: 0 },
                  width: { xs: '100%', mobile: 'auto' },
              };

        return (
            <Box
                sx={
                    stacked
                        ? { display: 'flex', flexDirection: 'column', width: '100%', minWidth: 0 }
                        : {
                              display: 'flex',
                              alignItems: isMobile ? 'flex-start' : 'center',
                              width: inlineHeader ? { xs: '100%', sm: 'auto' } : '100%',
                              maxWidth: '100%',
                              minWidth: 0,
                          }
                }
            >
                {label ? (
                    <Box component="div" id={groupLabelId}>
                        <Box
                            sx={[
                                isMobile && { marginTop: 1 },
                                { width: 160 },
                                ...(Array.isArray(labelBoxSx) ? labelBoxSx : [labelBoxSx]),
                            ]}
                        >
                            {renderTooltipLabel({ tooltip, label })}
                        </Box>
                    </Box>
                ) : null}
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            width: inlineHeader ? { xs: '100%', sm: 'auto' } : '100%',
                            minWidth: 0,
                            flex: inlineHeader ? 0 : 1,
                        }}
                    >
                        <Stack
                            direction={stacked ? 'column' : { xs: 'column', mobile: 'row' }}
                            spacing={1}
                            sx={{
                                width: inlineHeader ? { xs: '100%', sm: 'auto' } : '100%',
                                minWidth: 0,
                                alignItems: 'stretch',
                            }}
                            role="group"
                            aria-labelledby={label ? groupLabelId : undefined}
                        >
                            <Box sx={dateColSx}>
                                <DatePicker
                                    label={placeholders?.[0] ?? 'Start Date'}
                                    localeText={{
                                        toolbarTitle: placeholders?.[0] ?? 'Start date',
                                    }}
                                    value={fixedValue[0]}
                                    onChange={(next) => handleSinglePickerChange([next, fixedValue[1]])}
                                    onAccept={(next) => handleAccept([next, fixedValue[1]])}
                                    minDate={minDate}
                                    maxDate={fixedValue[1] ?? maxDate}
                                    slotProps={{
                                        textField: startTextFieldSlot,
                                        openPickerButton: openPickerSlotProps,
                                        toolbar: { sx: mobileToolbarTitleSx },
                                    }}
                                />
                            </Box>
                            <Box sx={dateColSx}>
                                <DatePicker
                                    key={`dp-end-${pickerSyncKey}`}
                                    label={placeholders?.[1] ?? 'End Date'}
                                    localeText={{
                                        toolbarTitle: placeholders?.[1] ?? 'End date',
                                    }}
                                    value={fixedValue[1]}
                                    onChange={(next) => handleSinglePickerChange([fixedValue[0], next])}
                                    onAccept={(next) => handleAccept([fixedValue[0], next])}
                                    minDate={fixedValue[0] ?? minDate}
                                    maxDate={maxDate}
                                    slotProps={{
                                        textField: endTextFieldSlot,
                                        openPickerButton: openPickerSlotProps,
                                        toolbar: { sx: mobileToolbarTitleSx },
                                    }}
                                />
                            </Box>
                            {!isMobile && shortcuts.length > 0 ? (
                                <Box sx={{ flexShrink: 0, alignSelf: 'center' }}>
                                    <PresetsMenu
                                        shortcuts={shortcuts}
                                        onPick={(shortcut) => {
                                            shortcutSelected = shortcut;
                                            handleAccept(getDatesFromShortcut(shortcut));
                                        }}
                                    />
                                </Box>
                            ) : null}
                        </Stack>
                    </Box>
                </LocalizationProvider>
            </Box>
        );
    }

    return (
        <Box
            sx={
                stacked
                    ? { display: 'flex', flexDirection: 'column', width: '100%' }
                    : { display: 'flex', alignItems: isMobile ? 'flex-start' : 'center', width: '100%' }
            }
        >
            {label ? (
                <Box component="div" id={groupLabelId}>
                    <Box
                        sx={[
                            isMobile && { marginTop: 1 },
                            { width: 160 },
                            ...(Array.isArray(labelBoxSx) ? labelBoxSx : [labelBoxSx]),
                        ]}
                    >
                        {renderTooltipLabel({ tooltip, label })}
                    </Box>
                </Box>
            ) : null}
            <LocalizationProvider dateAdapter={AdapterDateFns}>
                <MuiDateRangePicker
                    data-testid={testId}
                    localeText={{ start: placeholders?.[0] ?? 'Start Date', end: placeholders?.[1] ?? 'End Date' }}
                    value={fixedValue}
                    // onAccept is triggered when start and end dates are committed
                    // onChange is triggered whenever any number in the start or end dates are changed
                    onAccept={handleAccept}
                    // the context is only returned through onChange. Context contains whether the user
                    // pressed on the shortcut or not. When shortcut is pressed, save the shortcut selected and
                    // return it through onAccept immediately. This is used for example by the dashboard to save the shortcut
                    // to local storage, to be able to recompute start and dates correctly upon restore
                    onChange={handleChange}
                    maxDate={maxDate}
                    minDate={minDate}
                    slotProps={{
                        shortcuts: {
                            items: shortcutsItems,
                            sx: {
                                '.MuiChip-root': {
                                    fontSize: 'inherit',
                                },
                            },
                        },
                        fieldRoot: {
                            width: compactInline ? 'auto' : '100%',
                            direction: { xs: 'column', mobile: 'row' },
                        },
                        fieldSeparator: {
                            display: 'none',
                        },
                        textField: ({ position }) => ({
                            sx: {
                                '>.MuiFormLabel-root:not(.MuiFormLabel-filled):not(.Mui-focused)': { top: -9 },
                                ...(compactInline ? { width: 154, minWidth: 0 } : {}),
                            },
                            inputProps: {
                                'data-testid': `${testId}${position === 'end' ? '-end' : '-start'}`,
                            },
                            InputProps: {
                                endAdornment: isMobile ? null : (
                                    <InputAdornment position="end">
                                        <Calendar />
                                    </InputAdornment>
                                ),
                            },
                            error: getErrorStatusForDateField(position),
                            helperText: getErrorStatusForDateField(position) && required ? 'Required' : undefined,
                        }),
                    }}
                    slots={{
                        toolbar: CustomDateRangePickerToolbar as FC<DateRangePickerToolbarProps<Date>>,
                    }}
                />
            </LocalizationProvider>
        </Box>
    );
};
